import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Nullable } from 'global';
import {
  dealBuilderApi,
  DealBuilderEntity,
  GetDealBuildersResponse,
  GetDealBuildersVariables,
  StartDealBuilderContactInput,
  StartDealBuilderResponse,
  StartDealBuilderVariables,
  UpsertDealBuilderContactsResponse,
  UpsertDealBuilderContactsVariables,
  UpsertDealBuilderEquipmentsResponse,
  UpsertDealBuilderEquipmentsVariables,
} from '../api';

interface InitialDealBuilderData {
  ownerPgIds: string[];
  accountContactIds: string[];
  equipmentId: Nullable<string>;
}

interface UseDealBuilderPersistenceResult {
  activeEntity: Nullable<DealBuilderEntity>;
  historyEntities: DealBuilderEntity[];
  loading: boolean;
  initialData: Nullable<InitialDealBuilderData>;
  setActiveEntity: (entity: DealBuilderEntity) => void;
  ensureDealBuilder: (contacts: StartDealBuilderContactInput[]) => Promise<Nullable<string>>;
  startNewQuote: () => Promise<Nullable<string>>;
  upsertContacts: (ownerPgIds: string[], accountContactIds: string[]) => void;
  upsertEquipments: (equipmentIds: string[], overrideProposalId?: string) => void;
}

export const useDealBuilderPersistence = (
  vendorOpportunityId: Nullable<string>,
  activeProposalId: Nullable<string> = null
): UseDealBuilderPersistenceResult => {
  const [createdEntity, setCreatedEntity] = useState<Nullable<DealBuilderEntity>>(null);
  const startingRef = useRef(false);

  const {
    data: queryData,
    loading,
    refetch,
  } = useQuery<GetDealBuildersResponse, GetDealBuildersVariables>(dealBuilderApi.getDealBuilders, {
    variables: { vendorOpportunityId: vendorOpportunityId! },
    skip: !vendorOpportunityId,
  });

  const fetchedEntities = queryData?.dealBuilder.getDealBuilders.entity ?? [];
  const defaultEntity = fetchedEntities[0] ?? null;
  const overrideEntity = activeProposalId
    ? (fetchedEntities.find(e => e.proposalId === activeProposalId) ?? null)
    : null;
  const activeEntity = createdEntity ?? overrideEntity ?? defaultEntity;

  const historyEntities = useMemo(() => {
    if (!activeEntity) return [];
    return fetchedEntities.filter(e => e.proposalId !== activeEntity.proposalId);
  }, [fetchedEntities, activeEntity]);

  const initialData = useMemo((): Nullable<InitialDealBuilderData> => {
    if (!activeEntity) return null;
    const ownerPgIds = (activeEntity.proposalContacts ?? []).filter(c => c.ownerPgId).map(c => c.ownerPgId!);
    const accountContactIds = (activeEntity.proposalContacts ?? [])
      .filter(c => c.accountContactId)
      .map(c => c.accountContactId!);
    const equipmentId = activeEntity.proposalEquipments?.[0]?.equipmentId ?? null;
    return { ownerPgIds, accountContactIds, equipmentId };
  }, [activeEntity]);

  const [startDealBuilderMutation] = useMutation<StartDealBuilderResponse, StartDealBuilderVariables>(
    dealBuilderApi.startDealBuilder
  );

  const [upsertContactsMutation] = useMutation<UpsertDealBuilderContactsResponse, UpsertDealBuilderContactsVariables>(
    dealBuilderApi.upsertDealBuilderContacts
  );

  const [upsertEquipmentsMutation] = useMutation<
    UpsertDealBuilderEquipmentsResponse,
    UpsertDealBuilderEquipmentsVariables
  >(dealBuilderApi.upsertDealBuilderEquipments);

  const ensureDealBuilder = useCallback(
    async (contacts: StartDealBuilderContactInput[]): Promise<Nullable<string>> => {
      if (!vendorOpportunityId || activeEntity || startingRef.current) return null;
      startingRef.current = true;
      const { data } = await startDealBuilderMutation({
        variables: { input: { vendorOpportunityId, contacts } },
      });
      const entity = data?.dealBuilder.startDealBuilder.entity ?? null;
      if (entity) {
        setCreatedEntity(entity);
      }
      return entity?.proposalId ?? null;
    },
    [vendorOpportunityId, activeEntity, startDealBuilderMutation]
  );

  const startNewQuote = useCallback(async (): Promise<Nullable<string>> => {
    if (!vendorOpportunityId) return null;
    const { data } = await startDealBuilderMutation({
      variables: { input: { vendorOpportunityId } },
    });
    const entity = data?.dealBuilder.startDealBuilder.entity ?? null;
    if (entity) {
      setCreatedEntity(entity);
      refetch();
    }
    return entity?.proposalId ?? null;
  }, [vendorOpportunityId, startDealBuilderMutation, refetch]);

  const contactsTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const equipmentsTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(contactsTimerRef.current);
      clearTimeout(equipmentsTimerRef.current);
    };
  }, []);

  const upsertContacts = useCallback(
    (ownerPgIds: string[], accountContactIds: string[]) => {
      const proposalId = activeEntity?.proposalId;
      if (!proposalId) return;
      clearTimeout(contactsTimerRef.current);
      contactsTimerRef.current = setTimeout(() => {
        const contacts: StartDealBuilderContactInput[] = [
          ...ownerPgIds.map(id => ({ ownerPgId: id })),
          ...accountContactIds.map(id => ({ accountContactId: id })),
        ];
        upsertContactsMutation({ variables: { input: { proposalId, contacts } } });
      }, 300);
    },
    [activeEntity, upsertContactsMutation]
  );

  const upsertEquipments = useCallback(
    (equipmentIds: string[], overrideProposalId?: string) => {
      const proposalId = overrideProposalId ?? activeEntity?.proposalId;
      if (!proposalId) return;
      clearTimeout(equipmentsTimerRef.current);
      equipmentsTimerRef.current = setTimeout(() => {
        upsertEquipmentsMutation({ variables: { input: { proposalId, equipmentIds } } });
      }, 300);
    },
    [activeEntity, upsertEquipmentsMutation]
  );

  return {
    activeEntity,
    historyEntities,
    loading,
    initialData,
    setActiveEntity: setCreatedEntity,
    ensureDealBuilder,
    startNewQuote,
    upsertContacts,
    upsertEquipments,
  };
};
